import { useState } from 'react';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useMedplumSearch } from './useMedplumSearch';
import { validateAmka } from '@utils/amka-validation';

export interface SearchCriteria {
  type: 'amka' | 'demographics';
  amka?: string;
  lastName?: string;
  dob?: string;
}

export interface PatientSearchResult {
  // Search methods
  searchByAmka: (amka: string) => Promise<void>;
  searchByDemographics: (lastName: string, birthDate?: string | null) => Promise<void>;
  resetSearch: () => void;

  // State
  results: Patient[];
  searching: boolean;
  hasSearched: boolean;
  amkaError: string | null;
  demographicError: string | null;
  lastSearchCriteria: SearchCriteria | null;

  // Validation
  validateAmka: (amka: string) => boolean;
}

/**
 * Custom hook for patient search functionality
 * Supports searching by AMKA or demographics (last name + optional DOB)
 *
 * @returns PatientSearchResult object with search methods and state
 */
export function usePatientSearch(): PatientSearchResult {
  const medplum = useMedplum();
  const { findPersonByAmka } = useMedplumSearch();

  // Search state
  const [results, setResults] = useState<Patient[]>([]);
  const [searching, setSearching] = useState(false);
  const [amkaError, setAmkaError] = useState<string | null>(null);
  const [demographicError, setDemographicError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchCriteria, setLastSearchCriteria] = useState<SearchCriteria | null>(null);

  /**
   * Search for patient by AMKA
   *
   * @param amka - 11-digit AMKA number
   * @returns Promise that resolves when search is complete
   */
  const searchByAmka = async (amka: string): Promise<void> => {
    setAmkaError(null);
    setResults([]);
    setHasSearched(false);

    if (!validateAmka(amka)) {
      setAmkaError('AMKA must be exactly 11 digits');
      return;
    }

    setSearching(true);
    try {
      const patient = await findPersonByAmka(amka);
      if (patient) {
        setResults([patient]);
      } else {
        setResults([]);
      }
      setLastSearchCriteria({ type: 'amka', amka });
      setHasSearched(true);
    } catch (err) {
      console.error('Error searching by AMKA:', err);
      setAmkaError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  /**
   * Search for patients by demographics (last name + optional DOB)
   *
   * @param lastName - Patient's last name (minimum 3 characters)
   * @param birthDate - Optional date of birth (ISO string YYYY-MM-DD)
   * @returns Promise that resolves when search is complete
   */
  const searchByDemographics = async (
    lastName: string,
    birthDate?: string | null
  ): Promise<void> => {
    setDemographicError(null);
    setResults([]);
    setHasSearched(false);

    if (lastName.length < 3) {
      setDemographicError('Last name must be at least 3 characters');
      return;
    }

    setSearching(true);
    try {
      const searchParams: any = {
        family: lastName,
        _count: '50',
      };

      // Add birthdate to search only if provided (already in ISO format YYYY-MM-DD)
      if (birthDate) {
        searchParams.birthdate = birthDate;
      }

      const patients = await medplum.searchResources('Patient', searchParams);
      setResults(patients);

      // Convert ISO date to localized string for display
      let dobDisplay: string | undefined;
      if (birthDate) {
        const date = new Date(birthDate);
        dobDisplay = date.toLocaleDateString('el-GR');
      }

      setLastSearchCriteria({
        type: 'demographics',
        lastName,
        dob: dobDisplay,
      });
      setHasSearched(true);
    } catch (err) {
      console.error('Error searching by demographics:', err);
      setDemographicError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  /**
   * Reset all search state
   */
  const resetSearch = (): void => {
    setResults([]);
    setSearching(false);
    setAmkaError(null);
    setDemographicError(null);
    setHasSearched(false);
    setLastSearchCriteria(null);
  };

  return {
    // Methods
    searchByAmka,
    searchByDemographics,
    resetSearch,
    validateAmka,

    // State
    results,
    searching,
    hasSearched,
    amkaError,
    demographicError,
    lastSearchCriteria,
  };
}
